import type { SearchResultType } from '@fos/shared/contracts/search';
import { useEffect } from 'react';
import { useSession } from './auth';

/**
 * „Ostatnio otwierane” w ⌘K (Figma 13 Szukaj): karty klientów, projektów, zadań i zasobów otwarte w tej
 * przeglądarce. Tylko wygoda: localStorage per osoba i workspace (inna osoba na tym samym komputerze ich nie
 * widzi), bez danych wrażliwych (tytuł i ścieżka), brak dostępu do localStorage = pusta lista.
 */
export interface RecentItem {
  type: SearchResultType;
  id: string;
  title: string;
  subtitle: string;
  to: string;
  /** ms od epoki */
  at: number;
}

const MAX_ITEMS = 6;
const PREFIX = 'fos.recent';

export function recentStorageKey(userId: string, workspaceId: string): string {
  return `${PREFIX}:${workspaceId}:${userId}`;
}

function isRecentItem(v: unknown): v is RecentItem {
  const o = v as Partial<RecentItem> | null;
  return (
    !!o &&
    typeof o.id === 'string' &&
    typeof o.title === 'string' &&
    typeof o.to === 'string' &&
    o.to.startsWith('/') &&
    typeof o.type === 'string' &&
    typeof o.subtitle === 'string'
  );
}

export function readRecent(key: string | null): RecentItem[] {
  if (!key) return [];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isRecentItem).slice(0, MAX_ITEMS) : [];
  } catch {
    return [];
  }
}

export function pushRecent(key: string | null, item: Omit<RecentItem, 'at'>): void {
  if (!key) return;
  try {
    const rest = readRecent(key).filter((r) => !(r.type === item.type && r.id === item.id));
    localStorage.setItem(key, JSON.stringify([{ ...item, at: Date.now() }, ...rest].slice(0, MAX_ITEMS)));
  } catch {
    // tryb prywatny / brak miejsca: bez zapamiętywania
  }
}

/** Klucz localStorage zalogowanej osoby (null bez sesji). */
export function useRecentKey(): string | null {
  const { data } = useSession();
  if (!data?.authenticated || !data.workspace) return null;
  return recentStorageKey(data.user.id, data.workspace.id);
}

/** Zapamiętuje otwartą kartę (wywołaj na stronie szczegółów, gdy dane są wczytane). */
export function useRememberRecent(item: Omit<RecentItem, 'at'> | null): void {
  const key = useRecentKey();
  const id = item?.id;
  const title = item?.title;
  // biome-ignore lint/correctness/useExhaustiveDependencies: zapis przy zmianie rekordu lub tytułu
  useEffect(() => {
    if (item) pushRecent(key, item);
  }, [key, id, title]);
}
